/**
 * Route definition for /authors
 *
 * Uses TanStack Router's loader to prefetch authors data server-side.
 * Fetches the list of registered users/members before render.
 *
 * @module routes/authors
 * @category Routes
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getAuthors, ApiError } from '@/lib/api'
import AuthorsContainer from '@/containers/Authors'
import { createItemListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches authors list and returns it
 * Client-side: provides cached data if available, refetches if stale
 *
 * @returns {Promise<{initialAuthors: any[]}>}
 */
export const Route = createFileRoute('/$locale/authors')({
  /**
   * Preloads the authors data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   */
  loader: async () => {
    try {
      // Fetch authors list using typed API
      const result = await getAuthors({
        sort: 'lastActive',
        search: '',
      })

      // Return the data array to be available via Route.useLoaderData()
      return {
        initialAuthors: result.data,
      }
    } catch (error) {
      // Log error, show toast, and propagate to error boundary
      console.error('Failed to load authors:', error)
      toast.error('Failed to load authors. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the authors page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: AuthorsPage,
})

/**
 * Authors page component.
 *
 * Receives preloaded authors data from the loader and passes it to
 * the AuthorsContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * @returns {React.ReactElement}
 */
function AuthorsPage() {
  // Get the preloaded data from the route loader
  const { initialAuthors } = Route.useLoaderData()

  const pageUrl = 'https://stealthisnft.xyz/authors'

  // Convert authors to items for structured data
  const authorItems = initialAuthors?.slice(0, 20).map(author => ({
    name: author?.username || author?.display_name || 'Unknown User',
    description: author?.bio || '',
    image: author?.avatar || author?.image || '',
    url: author?.username ? `https://stealthisnft.xyz/profile/${author.username}` : '',
  })) || []

  const structuredData = authorItems.length > 0
    ? createItemListSchema({
        title: t('meta.authors.title'),
        description: t('meta.authors.description'),
        url: pageUrl,
        items: authorItems,
      })
    : undefined

  // Set metadata including dynamic JSON-LD
  setPageMetadata({
    title: t('meta.authors.title'),
    description: t('meta.authors.description'),
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData || null,
  })

  return (
    <AuthorsContainer initialAuthors={initialAuthors} />
  )
}
